import { domToPng } from 'modern-screenshot'
import React, { ReactNode, createContext, useContext, useState } from 'react'

type CardExportContextType = {
  isModalOpen: boolean
  toggleModal: () => void
  shareImage: () => Promise<void>
  exportCardHQ: () => Promise<void>
}

const CardExportContext = createContext<CardExportContextType | undefined>(
  undefined,
)

export function CardExportProvider({ children }: { children: ReactNode }) {
  const [isModalOpen, setIsModalOpen] = useState(false)

  function toggleModal() {
    if (isModalOpen) {
      document.body.style.overflow = 'unset'
    }
    setIsModalOpen(!isModalOpen)

    if (!isModalOpen) {
      setTimeout(() => {
        exportCard()
      }, 700)
    }
  }

  async function exportCard() {
    const cardRef = document.getElementById(
      'cardContainer',
    ) as HTMLElement | null
    const exportedCardContainer = document.getElementById('exportedCard')

    if (cardRef && exportedCardContainer) {
      if (exportedCardContainer.firstChild) {
        exportedCardContainer.removeChild(exportedCardContainer.firstChild)
      }

      const originalClasses = cardRef.className
      cardRef.classList.remove('lg:-rotate-3', 'lg:scale-[0.9]')
      cardRef.classList.remove('rotate-0')
      await new Promise((resolve) => requestAnimationFrame(resolve))

      try {
        const dataUrl = await domToPng(cardRef, {
          quality: 1.0,
          scale: 2,
          style: {
            transform: 'none',
            margin: '0',
          },
        })
        cardRef.className = originalClasses

        const img = new Image()
        img.onload = () => {
          img.style.maxWidth = '100%'
          img.style.height = 'auto'
          img.style.display = 'block'

          exportedCardContainer.appendChild(img)
        }
        img.onerror = (error) => {
          console.error('Error loading image data URL:', error)
          cardRef.className = originalClasses
        }
        img.src = dataUrl
      } catch (error) {
        cardRef.className = originalClasses
        console.error('modern-screenshot:', error)
      }
    } else {
      console.error('Card container or export container not found')
    }
  }

  async function shareImage() {
    const exportedCardContainer = document.getElementById('exportedCard')
    const imgElement = exportedCardContainer?.querySelector(
      'img',
    ) as HTMLImageElement | null

    if (
      !imgElement ||
      !imgElement.src ||
      !imgElement.src.startsWith('data:image/png')
    ) {
      return
    }

    try {
      const response = await fetch(imgElement.src)
      const blob = await response.blob()

      if (!blob) {
        return
      }

      const file = new File([blob], 'Goodfellas Card.png', { type: blob.type })
      const shareData = {
        files: [file],
        title: 'Goodfellas Card',
        text: 'Confira meu card do Goodfellas!',
      }

      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare(shareData)
      ) {
        try {
          await navigator.share(shareData)
        } catch (err: any) {
          if (err.name !== 'AbortError') {
            triggerDownload(blob, 'Goodfellas Card.png')
          }
        }
      } else {
        triggerDownload(blob, 'Goodfellas Card.png')
      }
    } catch (error) {
      console.error('Error fetching or processing image for sharing:', error)
    }
  }

  function triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  async function exportCardHQ() {
    const cardRef = document.getElementById(
      'cardContainer',
    ) as HTMLElement | null

    if (!cardRef) {
      console.error('Card container not found')
      return
    }

    const originalClasses = cardRef.className
    cardRef.classList.remove('lg:-rotate-3', 'lg:scale-[0.9]')
    cardRef.classList.remove('rotate-0')
    await new Promise((resolve) => requestAnimationFrame(resolve))

    try {
      const cardDataUrl = await domToPng(cardRef, {
        quality: 1.0,
        scale: 4,
        style: {
          transform: 'none',
          margin: '0',
        },
      })
      cardRef.className = originalClasses

      const img = new Image()
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = reject
        img.src = cardDataUrl
      })

      const targetHeight = 1080
      const aspectRatio = img.width / img.height
      const targetWidth = Math.round(targetHeight * aspectRatio)

      const canvas = document.createElement('canvas')
      canvas.width = targetWidth
      canvas.height = targetHeight
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        throw new Error('Could not get canvas context')
      }

      ctx.drawImage(img, 0, 0, targetWidth, targetHeight)

      canvas.toBlob(
        (blob) => {
          if (blob) {
            triggerDownload(blob, 'Goodfellas Card HQ.png')
          }
        },
        'image/png',
        1.0,
      )
    } catch (error) {
      cardRef.className = originalClasses
      console.error('Error exporting high-quality card:', error)
    }
  }

  return (
    <CardExportContext.Provider
      value={{ isModalOpen, toggleModal, shareImage, exportCardHQ }}
    >
      {children}
    </CardExportContext.Provider>
  )
}

export function useCardExport() {
  const context = useContext(CardExportContext)
  if (!context) {
    throw new Error('useCardExport must be used within CardExportProvider')
  }
  return context
}
